import React from 'react';

import { useAppSelector } from '@/store';

import { BrandFadeHost, BrandFadeContent } from './BrandTransition.styles';

/**
 * Cross-page brand-switch transition.
 *
 * Wraps each fixed route's page content (see `router.tsx`) so that whenever the
 * active brand changes the *whole view* fades out, swaps to the new brand's data
 * while hidden, then fades back in — on any page, regardless of where its data
 * comes from. The phase timeline lives in `brandSwitchMiddleware`
 * (`store/slices/casino-instance`); this component just maps the current
 * `brandSwitchPhase` to the page's opacity:
 *
 *   out  → page fading out (data swaps at the bottom of the fade, while hidden)
 *   in   → page (new data) fading in
 *   idle → page fully visible
 *
 * It's an opacity gate only — it never freezes or remounts the page, so the live
 * page keeps its scroll position and simply re-renders with the new brand's data
 * while hidden.
 */
export const BrandTransition = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const phase = useAppSelector((state) => state.casinoInstance.brandSwitchPhase);

  const pageHidden = phase === 'out';

  return (
    <BrandFadeHost>
      <BrandFadeContent $hidden={pageHidden}>{children}</BrandFadeContent>
    </BrandFadeHost>
  );
};

export default BrandTransition;
